import { useMemo, useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";

import { CaseSchema } from "@api/generated/types.gen";

import { CAPABILITIES } from "@constants/capabilities";
import { NAVIGATION_PATHS } from "@constants/navigation";
import { getServiceTypeLabel as serviceLabel } from "@constants/service-types";

import { useCases } from "@hooks/queries/use-cases";
import { useCapabilities } from "@hooks/queries/use-me";

import { formatNullableDateToLocal } from "@utils/dateUtils";
import { cn } from "@utils/ui";

// Status → design pill (Unscheduled amber / In progress blue / Confirmed green /
// Completed grey), matching the referrer portal Cases table.
const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  TO_BE_PROCESSED: { label: "Unscheduled", cls: "bg-[#fdf0dc] text-[#B26A00]" },
  PROCESSING: { label: "In progress", cls: "bg-[#e6f0fb] text-[#0065CB]" },
  EXPORTING: { label: "In progress", cls: "bg-[#e6f0fb] text-[#0065CB]" },
  EXPORTED: { label: "Confirmed", cls: "bg-[#e7f6ec] text-[#16794F]" },
  COMPLETED: { label: "Completed", cls: "bg-[#eef1f6] text-[#5A5A6B]" },
};

const TABS = ["All", "Open", "Completed"] as const;
type Tab = (typeof TABS)[number];

const th =
  "px-3 py-3 text-left text-[11px] font-bold uppercase tracking-[0.04em] text-[#5A5A6B]";
const td = "px-3 py-[13px] text-[12.5px] text-[#5A5A6B] align-middle";

/**
 * Cases — the referrer portal case list (design-matched). Own cases (row-scoped
 * by the backend); rows open the Cases → Case summary detail. Filter pills +
 * search + status pills per the MEDirect 2.0 design.
 */
const CasesPage = () => {
  const navigate = useNavigate();
  const { can } = useCapabilities();
  const [tab, setTab] = useState<Tab>("All");
  const [search, setSearch] = useState("");

  const { data: cases = [], isLoading } = useCases();

  const counts = useMemo(() => {
    const open = cases.filter((c) => c.status !== "COMPLETED").length;
    const completed = cases.filter((c) => c.status === "COMPLETED").length;
    return { All: cases.length, Open: open, Completed: completed };
  }, [cases]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cases.filter((c) => {
      if (tab === "Open" && c.status === "COMPLETED") return false;
      if (tab === "Completed" && c.status !== "COMPLETED") return false;
      if (!q) return true;
      return [c.claim_number, c.examinee_name, c.case_number]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q));
    });
  }, [cases, tab, search]);

  const open = (c: CaseSchema) =>
    navigate({ to: `${NAVIGATION_PATHS.CASES}/${c.case_number}` });

  return (
    <div className="min-h-full bg-[#F5F7FA] px-[34px] py-[26px]">
      {/* Header */}
      <div className="mb-[18px] flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-0.01em] text-[#1A1939]">
            Cases
          </h1>
          <p className="text-sm text-[#5A5A6B]">
            All your referrals in one place. Click a claim number to open the case.
          </p>
        </div>
        {/* MDRS-230 UAT-05: case creation is gated on CASES_CREATE. Previously
            ungated here, so a role with "create case" switched off in the access
            config still saw this button and could reach the /new-case wizard. */}
        {can(CAPABILITIES.CASES_CREATE) && (
          <button
            type="button"
            onClick={() => navigate({ to: NAVIGATION_PATHS.NEW_CASE })}
            className="flex items-center gap-2 rounded-[10px] bg-[#0065CB] px-[18px] py-2.5 text-[13.5px] font-semibold text-white shadow-[0_2px_8px_rgba(0,101,203,0.28)]"
          >
            <Plus className="h-[15px] w-[15px]" /> New case
          </button>
        )}
      </div>

      {/* Filters + search */}
      <div className="mb-4 flex flex-wrap items-center gap-3.5">
        <div className="flex items-center gap-1.5 rounded-[11px] border border-[#E2E5EB] bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                "flex items-center gap-[7px] rounded-lg px-3.5 py-[7px] text-[13px] font-semibold",
                tab === t
                  ? "bg-[#e8f2fb] text-[#0065CB]"
                  : "text-[#5A5A6B] hover:bg-[#f7f9fc]"
              )}
            >
              {t}
              <span className="text-[11px] font-bold opacity-75">{counts[t]}</span>
            </button>
          ))}
        </div>
        <div className="relative flex min-w-[220px] max-w-[360px] flex-1 items-center">
          <Search className="pointer-events-none absolute left-3 h-4 w-4 text-[#8A8A98]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by claim, claimant or expert"
            className="w-full rounded-[10px] border border-[#E2E5EB] bg-white py-[9px] pl-[34px] pr-3 text-[13.5px] outline-none focus:border-[#0065CB]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-[14px] border border-[#E2E5EB] bg-white shadow-[0_2px_8px_rgba(26,25,57,0.05)]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px] border-collapse">
            <thead>
              <tr className="border-b border-[#E2E5EB] bg-[#fafbfd]">
                <th className={cn(th, "pl-[18px]")}>Claim no</th>
                <th className={th}>Claimant</th>
                <th className={th}>Service type</th>
                <th className={th}>Medical expert</th>
                <th className={th}>Appointment</th>
                <th className={th}>Status</th>
                <th className={th}>Report due</th>
                <th className={cn(th, "pr-[18px]")}>Documents</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const pill =
                  STATUS_PILL[c.status ?? "TO_BE_PROCESSED"] ??
                  STATUS_PILL.TO_BE_PROCESSED;
                const completed = c.status === "COMPLETED";
                const svc = c.service_type
                  ? serviceLabel(c.service_type) || c.service_type
                  : "—";
                return (
                  <tr
                    key={c.id}
                    onClick={() => open(c)}
                    className="cursor-pointer border-b border-[#eef1f6] hover:bg-[#f7f9fc]"
                  >
                    <td className={cn(td, "pl-[18px]")}>
                      <span className="text-[13.5px] font-bold text-[#0065CB]">
                        {c.claim_number || c.case_number}
                      </span>
                    </td>
                    <td className={cn(td, "text-[13px] font-medium text-[#1A1939]")}>
                      {c.examinee_name || "—"}
                    </td>
                    <td className={cn(td, "max-w-[180px]")}>{svc}</td>
                    <td className={cn(td, "whitespace-nowrap text-[#1A1939]")}>
                      Not yet assigned
                    </td>
                    <td className={cn(td, "whitespace-nowrap")}>
                      {formatNullableDateToLocal(c.appointment_date) || "—"}
                    </td>
                    <td className={td}>
                      <span
                        className={cn(
                          "whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold",
                          pill.cls
                        )}
                      >
                        {pill.label}
                      </span>
                    </td>
                    <td className={cn(td, "whitespace-nowrap")}>
                      {formatNullableDateToLocal(c.report_due_date) || "TBC"}
                    </td>
                    <td className={cn(td, "pr-[18px]")}>
                      {completed ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            open(c);
                          }}
                          className="rounded-lg border border-[#cdddfb] bg-white px-[13px] py-1.5 text-xs font-semibold text-[#0065CB]"
                        >
                          Download report
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs text-[#8A8A98]">
                          <span className="h-[7px] w-[7px] flex-none rounded-full bg-[#8A8A98]" />
                          Awaiting documents
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!isLoading && rows.length === 0 && (
          <div className="px-5 py-[34px] text-center text-[13px] text-[#8A8A98]">
            No cases match your filters.
          </div>
        )}
        {isLoading && (
          <div className="px-5 py-[34px] text-center text-[13px] text-[#8A8A98]">
            Loading…
          </div>
        )}
      </div>
    </div>
  );
};

export default CasesPage;
